import {
    PUBLIC_LANGUAGES,
    type CostKey,
    type PublicLanguage,
    type PublicSeverity,
} from '@/entities/publicReport';

import type { FindingFilter } from './reportIndex';

/**
 * Teksten van de klantpagina.
 *
 * Nederlands is hier **u-vorm** (principe 10); in de app is alles jij-vorm - de
 * teksten van de kantoorpreview staan daarom apart in `previewCopy`.
 * Termen volgen `docs/woordenlijst.md`: de kostenblokken heten Direct, Op
 * termijn en Verbetering, zonder jaartal.
 *
 * De koper kan de taal omzetten zodra de server vertaalt (`tenant.languages`); de
 * omlijsting komt uit dit bestand, met de hand vertaald, de inhoud van het rapport
 * uit de API, door de server vertaald. Een taal toevoegen: hier een `PublicCopy`,
 * in `PUBLIC_LANGUAGES` de code, en op de server de taal en zijn lang-bestanden.
 */

export const LANGUAGE_LABELS: Record<PublicLanguage, string> = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    es: 'Español',
    pl: 'Polski',
    tr: 'Türkçe',
    ar: 'العربية',
};

/** Arabisch met westerse cijfers: bedragen en datums zijn dezelfde als in het Nederlandse rapport. */
export const LANGUAGE_LOCALES: Record<PublicLanguage, string> = {
    nl: 'nl-NL',
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
    pl: 'pl-PL',
    tr: 'tr-TR',
    ar: 'ar-u-nu-latn',
};

export type PublicProblemKey =
    'notFound' | 'expired' | 'revoked' | 'tooManyAttempts' | 'offline' | 'unknown';

export interface PublicCopy {
    documentTitle: string;
    languageLabel: string;
    secureLabel: string;
    loading: string;
    reportReady: string;
    validUntil: (date: string) => string;
    costsTitle: string;
    costsSubtitle: string;
    costsTotal: string;
    costLabels: Record<CostKey, string>;
    costParts: (count: number) => string;
    nhgTitle: string;
    nhgSubtitle: string;
    nhgColumns: {
        code: string;
        element: string;
        locations: string;
        direct: string;
        termijn: string;
    };
    nhgTotal: string;
    nhgImprovement: string;
    nhgOutside: (amount: string) => string;
    nhgEmpty: string;
    findingsTitle: string;
    /** Voor de regel "Locatie: rechterzijde, tweede raam" onder een bevinding. */
    findingLocation: string;
    findingsShowAll: (count: number) => string;
    findingsShowFewer: string;
    urgencyLabels: Record<CostKey, string>;
    partsTitle: string;
    partsNote: string;
    severityLabels: Record<PublicSeverity, string>;
    energyUsageUnit: string;
    energyLabelPrefix: string;
    questionsTitle: string;
    inspectorRole: string;
    askOpen: string;
    askLabel: string;
    askPlaceholder: string;
    askEmailLabel: string;
    askSubmit: string;
    askCancel: string;
    askSent: string;
    askTooShort: string;
    askEmailInvalid: string;
    askTooMany: string;
    callLabel: string;
    shareTitle: string;
    shareSubtitle: string;
    shareEmailLabel: string;
    sharePlaceholder: string;
    shareSubmit: string;
    shareSent: (email: string) => string;
    shareInvalid: string;
    shareDisabled: string;
    shareTooMany: string;
    sharedWith: (name: string) => string;
    invoiceLabel: string;
    invoiceDownload: string;
    accessCodeTitle: string;
    accessCodeBody: string;
    accessCodeLabel: string;
    accessCodeSubmit: string;
    accessCodeInvalid: string;
    accessCodeAttemptsLeft: (attempts: number) => string;
    accessCodeEmpty: string;
    problemTitles: Record<PublicProblemKey, string>;
    problemBodies: Record<PublicProblemKey, string>;
    expiredOn: (date: string) => string;
    tooManyAttemptsWait: (minutes: number) => string;
    problemHelp: (phone: string) => string;
    problemHelpNoPhone: string;
    actionFailed: string;
    retry: string;
    /** De tabbladen: overzicht, kosten, het volledige rapport en de NHG-bijlage. */
    tabsLabel: string;
    tabOverview: string;
    tabCosts: string;
    tabReport: string;
    tabNhg: string;
    /** In de balk boven het rapport, terug naar de lijst met onderdelen. */
    backToOverview: string;
    backToTop: string;
    /** De kernzin van de samenvatting bovenaan het overzicht: wat de keuring vond. */
    headline: (defects: number, attention: number) => string;
    /** Onder de kernzin: naar de hele samenvatting in het rapport. */
    readSummary: string;
    /** Onder de titel van de lijst met onderdelen in het overzicht. */
    outlineIntro: string;
    /** Voorgelezen naam van de oordelen in hun kleur, boven die lijst. */
    judgementLegend: string;
    /** Naast die oordelen: naar "Zo leest u dit rapport" in het rapport. */
    judgementLegendLink: string;
    /** De tabel met de kosten per onderdeel. */
    costTableTitle: string;
    costTableIntro: string;
    coverInspectedAt: string;
    coverInspector: string;
    coverOrderNumber: string;
    coverReport: string;
    coverVersion: (version: number, date: string | null) => string;
    /** Onder een onderdeel zonder bevindingen en zonder toelichting, zoals in de PDF. */
    partQuiet: string;
    costsColumnPart: string;
    costsColumnGroup: string;
    costsTotalRow: string;
    nhgRow: (code: string) => string;
    /** Het formulier voor de geldverstrekker staat alleen in de PDF. */
    nhgAdministration: string;
    photoOpen: (caption: string | null, position: number, count: number) => string;
    photoTitle: string;
    photoClose: string;
    photoPrevious: string;
    photoNext: string;
    photoPosition: (position: number, count: number) => string;
    /** Op de downloadknop terwijl de PDF binnenkomt. */
    pdfBusy: string;
    /** Onder de downloadknop als de server de PDF nog niet kan geven. */
    pdfUnavailable: string;
    /** De filterbalk boven het volledige rapport. */
    filterLabel: string;
    filters: Record<FindingFilter, string>;
    /** "7 van 23 bevindingen"; zonder filter "23 bevindingen". */
    filterStatus: (shown: number, total: number, filtered: boolean) => string;
    filterEmpty: string;
    expandAll: string;
    collapseAll: string;
    /** In de kop van een hoofdstuk, ook als het is ingeklapt. */
    chapterFindings: (count: number) => string;
    chapterQuiet: string;
    /** De scheiding voor de bijlage: algemene toelichting en voorwaarden, achteraan. */
    appendixTitle: string;
    appendixNote: string;
    /** Op een kostenblok: wat een tik laat zien. */
    costFindings: (count: number) => string;
    costBreakdownEyebrow: string;
    costBreakdownIntro: string;
    costBreakdownRest: string;
    costBreakdownTotal: string;
    costBreakdownClose: string;
    /** Voorgelezen naam van een klein kostenblok dat de bevindingen erachter opent. */
    costBreakdownOpen: (label: string, amount: string) => string;
    /** Voorgelezen naam van de NHG-code onder een bevinding. */
    nhgJump: (code: string) => string;
    /** Bovenaan een vertaald rapport: de server vertaalde de inhoud, het Nederlands is leidend. */
    translationNotice: string;
    /** Terwijl de server het rapport in de gekozen taal vertaalt. */
    translating: string;
    /** Het vertalen lukte niet: de koper leest het Nederlands. */
    translationFailed: string;
}

const nl: PublicCopy = {
    documentTitle: 'Uw keuringsrapport',
    languageLabel: 'Taal',
    secureLabel: 'beveiligd',
    loading: 'Uw rapport wordt geladen',
    reportReady: 'Rapport gereed',
    validUntil: (date) => `Deze link werkt tot ${date}.`,
    costsTitle: 'Wat kost het herstel?',
    costsSubtitle: 'Indicatieve ramingen inclusief btw, voor herstel door een vakbekwaam bedrijf.',
    costsTotal: 'Totaal geraamd',
    costLabels: { direct: 'Direct', termijn: 'Op termijn', onderhoud: 'Verbetering' },
    costParts: (count) => (count === 1 ? '1 onderdeel' : `${count} onderdelen`),
    nhgTitle: 'NHG-bijlage',
    nhgSubtitle:
        'Voor een hypotheek met Nationale Hypotheek Garantie: de noodzakelijke kosten per onderdeel, zoals in het Model bouwkundig rapport NHG in de PDF.',
    nhgColumns: {
        code: 'Code',
        element: 'Onderdeel',
        locations: 'Hoofdstuk',
        direct: 'Direct noodzakelijk',
        termijn: 'Op termijn noodzakelijk',
    },
    nhgTotal: 'Totaal woning',
    nhgImprovement: 'Verbetering',
    nhgOutside: (amount) =>
        `${amount} aan kosten uit het rapport staat niet in de bijlage, omdat die kosten bij geen onderdeel van het NHG-model horen of niet voor NHG zijn opgenomen. Daarom is het totaal hier lager dan bij ‘Wat kost het herstel?’.`,
    nhgEmpty: 'In de NHG-bijlage staan geen noodzakelijke kosten.',
    findingsTitle: 'Belangrijkste bevindingen',
    findingLocation: 'Locatie',
    findingsShowAll: (count) => `Alle ${count} bevindingen tonen`,
    findingsShowFewer: 'Minder bevindingen tonen',
    urgencyLabels: { direct: 'Direct', termijn: 'Op termijn', onderhoud: 'Verbetering' },
    partsTitle: 'Alle onderdelen',
    partsNote: 'In het PDF-rapport staat elk onderdeel uitgewerkt met foto’s en toelichting.',
    severityLabels: { goed: 'Goed', aandacht: 'Aandachtspunt', gebrek: 'Gebrek' },
    energyUsageUnit: 'kWh/m²·jr',
    energyLabelPrefix: 'Energielabel',
    questionsTitle: 'Vragen over het rapport?',
    inspectorRole: 'Uw inspecteur',
    askOpen: 'Vraag stellen',
    askLabel: 'Uw vraag',
    askPlaceholder: 'Waar gaat uw vraag over?',
    askEmailLabel: 'Uw e-mailadres, voor het antwoord (niet verplicht)',
    askSubmit: 'Vraag versturen',
    askCancel: 'Annuleren',
    askSent: 'Uw vraag is verstuurd naar uw keuringsbedrijf.',
    askTooShort: 'Schrijf uw vraag in een paar woorden.',
    askEmailInvalid: 'Vul een geldig e-mailadres in, of laat het veld leeg.',
    askTooMany: 'U heeft net al een paar vragen gestuurd. Probeert u het over een uur opnieuw.',
    callLabel: 'Bellen',
    shareTitle: 'Delen',
    shareSubtitle:
        'Stuur het rapport naar uw hypotheekadviseur of makelaar. Zij hebben geen account nodig.',
    shareEmailLabel: 'E-mailadres',
    sharePlaceholder: 'naam@voorbeeld.nl',
    shareSubmit: 'Versturen',
    shareSent: (email) => `Het rapport is verstuurd naar ${email}.`,
    shareInvalid: 'Vul een geldig e-mailadres in.',
    shareDisabled: 'Doorsturen staat voor dit rapport uit. Uw keuringsbedrijf kan u verder helpen.',
    shareTooMany:
        'U heeft het rapport net al een paar keer doorgestuurd. Probeert u het over een uur opnieuw.',
    sharedWith: (name) => `Gedeeld met ${name}`,
    invoiceLabel: 'Factuur',
    invoiceDownload: 'Factuur (PDF)',
    accessCodeTitle: 'Voer uw toegangscode in',
    accessCodeBody:
        'Dit rapport is afgeschermd met een toegangscode. U krijgt de code per sms, los van de link. Geen sms ontvangen? Uw keuringsbedrijf helpt u verder.',
    accessCodeLabel: 'Toegangscode',
    accessCodeSubmit: 'Rapport openen',
    accessCodeInvalid:
        'Deze code klopt niet. Controleer de code uit de sms van uw keuringsbedrijf.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1
            ? 'U kunt het nog één keer proberen.'
            : `U kunt het nog ${attempts} keer proberen.`,
    accessCodeEmpty: 'Vul uw toegangscode in.',
    problemTitles: {
        notFound: 'Deze link werkt niet',
        expired: 'Deze link is verlopen',
        revoked: 'Deze link is ingetrokken',
        tooManyAttempts: 'Te vaak een onjuiste code',
        offline: 'Geen verbinding',
        unknown: 'Het rapport kan nu niet worden getoond',
    },
    problemBodies: {
        notFound: 'Controleer of u de volledige link uit de e-mail of sms heeft gebruikt.',
        expired: 'Vraag uw keuringsbedrijf om een nieuwe link.',
        revoked: 'Het keuringsbedrijf heeft deze link gesloten. Vraag gerust om een nieuwe link.',
        tooManyAttempts:
            'Om uw rapport te beschermen kunt u het even niet opnieuw proberen. Probeert u het over een kwartier nog eens.',
        offline: 'Uw apparaat heeft geen internetverbinding. Probeer het zo opnieuw.',
        unknown: 'Probeer het over een paar minuten opnieuw.',
    },
    expiredOn: (date) =>
        `Deze link was geldig tot ${date}. Vraag uw keuringsbedrijf om een nieuwe link.`,
    tooManyAttemptsWait: (minutes) =>
        `Om uw rapport te beschermen kunt u het even niet opnieuw proberen. Probeert u het over ${minutes === 1 ? 'een minuut' : `${minutes} minuten`} nog eens.`,
    problemHelp: (phone) => `Neem contact op via ${phone}.`,
    problemHelpNoPhone: 'Neem contact op met uw keuringsbedrijf.',
    actionFailed: 'Dat lukte nu niet. Probeer het over een paar minuten opnieuw.',
    retry: 'Opnieuw proberen',
    tabsLabel: 'Weergave van het rapport',
    tabOverview: 'Overzicht',
    tabCosts: 'Kosten',
    tabReport: 'Rapport',
    tabNhg: 'NHG',
    backToOverview: 'Terug naar overzicht',
    backToTop: 'Terug naar boven',
    headline: (defects, attention) => {
        const gebreken = defects === 1 ? '1 gebrek' : `${defects} gebreken`;
        const punten = attention === 1 ? '1 aandachtspunt' : `${attention} aandachtspunten`;

        if (defects === 0 && attention === 0) {
            return 'Er zijn geen gebreken of aandachtspunten vastgesteld.';
        }

        if (attention === 0) {
            return `Er ${defects === 1 ? 'is' : 'zijn'} ${gebreken} vastgesteld.`;
        }

        if (defects === 0) {
            return `Er ${attention === 1 ? 'is' : 'zijn'} ${punten} vastgesteld.`;
        }

        return `Er zijn ${gebreken} en ${punten} vastgesteld.`;
    },
    readSummary: 'Lees de hele samenvatting',
    outlineIntro: 'Kies een onderdeel om het in het rapport te lezen.',
    judgementLegend: 'De oordelen en hun kleur',
    judgementLegendLink: 'Zo leest u dit rapport',
    costTableTitle: 'Kosten per onderdeel',
    costTableIntro: 'Kies een onderdeel om de bevindingen erachter in het rapport te lezen.',
    coverInspectedAt: 'Datum opname',
    coverInspector: 'Inspecteur',
    coverOrderNumber: 'Dossiernummer',
    coverReport: 'Rapport',
    coverVersion: (version, date) => (date ? `versie ${version}, ${date}` : `versie ${version}`),
    partQuiet: 'Geen bijzonderheden.',
    costsColumnPart: 'Onderdeel',
    costsColumnGroup: 'Kostengroep',
    costsTotalRow: 'Totaal',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration:
        'De administratieve gegevens en de handtekening van de inspecteur staan in het PDF-rapport.',
    photoOpen: (caption, position, count) =>
        `Foto ${position} van ${count} groot bekijken${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Foto',
    photoClose: 'Foto sluiten',
    photoPrevious: 'Vorige foto',
    photoNext: 'Volgende foto',
    photoPosition: (position, count) => `${position} van ${count}`,
    pdfBusy: 'De pdf wordt opgehaald…',
    pdfUnavailable: 'De pdf is nog niet beschikbaar; probeer het later opnieuw.',
    filterLabel: 'Bevindingen filteren',
    filters: {
        alles: 'Alles',
        gebreken: 'Gebreken',
        aandacht: 'Aandachtspunten',
        kosten: 'Met kosten',
    },
    filterStatus: (shown, total, filtered) => {
        const noun = total === 1 ? 'bevinding' : 'bevindingen';

        return filtered ? `${shown} van ${total} ${noun}` : `${total} ${noun}`;
    },
    filterEmpty: 'Geen bevindingen in deze selectie.',
    expandAll: 'Alles uitklappen',
    collapseAll: 'Alles inklappen',
    chapterFindings: (count) => (count === 1 ? '1 bevinding' : `${count} bevindingen`),
    chapterQuiet: 'Geen bijzonderheden',
    appendixTitle: 'Toelichting en voorwaarden',
    appendixNote:
        'De algemene toelichting bij dit rapport en de voorwaarden waaronder deze keuring is uitgevoerd.',
    costFindings: (count) =>
        count === 1 ? 'Bekijk de bevinding' : `Bekijk de ${count} bevindingen`,
    costBreakdownEyebrow: 'Kosten',
    costBreakdownIntro:
        'Deze bevindingen tellen samen op tot dit bedrag. Kies er een om hem in het rapport te lezen.',
    costBreakdownRest: 'Overige posten',
    costBreakdownTotal: 'Totaal',
    costBreakdownClose: 'Sluiten',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: bekijk de bevindingen achter dit bedrag`,
    nhgJump: (code) => `NHG ${code}: naar deze regel in de NHG-bijlage`,
    translationNotice: 'Automatisch vertaald uit het Nederlands. De Nederlandse versie is leidend.',
    translating: 'Het rapport wordt vertaald…',
    translationFailed: 'Vertalen lukte nu niet. U leest de Nederlandse versie.',
};

const en: PublicCopy = {
    documentTitle: 'Your inspection report',
    languageLabel: 'Language',
    secureLabel: 'secure',
    loading: 'Loading your report',
    reportReady: 'Report ready',
    validUntil: (date) => `This link works until ${date}.`,
    costsTitle: 'What will the repairs cost?',
    costsSubtitle: 'Indicative estimates including VAT, for repairs by a qualified company.',
    costsTotal: 'Total estimate',
    costLabels: { direct: 'Now', termijn: 'Later', onderhoud: 'Improvement' },
    costParts: (count) => (count === 1 ? '1 item' : `${count} items`),
    nhgTitle: 'NHG annex',
    nhgSubtitle:
        'For a mortgage with the Dutch National Mortgage Guarantee (NHG): the necessary costs per building part, as in the NHG model report in the PDF.',
    nhgColumns: {
        code: 'Code',
        element: 'Building part',
        locations: 'Chapter',
        direct: 'Necessary now',
        termijn: 'Necessary later',
    },
    nhgTotal: 'Total',
    nhgImprovement: 'Improvement',
    nhgOutside: (amount) =>
        `${amount} of the costs in the report is not in the annex, because those costs belong to no part of the NHG model or were not included for NHG. That is why the total here is lower than under ‘What will the repairs cost?’.`,
    nhgEmpty: 'The NHG annex lists no necessary costs.',
    findingsTitle: 'Main findings',
    findingLocation: 'Location',
    findingsShowAll: (count) => `Show all ${count} findings`,
    findingsShowFewer: 'Show fewer findings',
    urgencyLabels: { direct: 'Now', termijn: 'Later', onderhoud: 'Improvement' },
    partsTitle: 'All items',
    partsNote: 'The PDF report covers every item with photos and an explanation.',
    severityLabels: { goed: 'Sound', aandacht: 'Attention point', gebrek: 'Defect' },
    energyUsageUnit: 'kWh/m²·yr',
    energyLabelPrefix: 'Energy label',
    questionsTitle: 'Questions about the report?',
    inspectorRole: 'Your inspector',
    askOpen: 'Ask a question',
    askLabel: 'Your question',
    askPlaceholder: 'What is your question about?',
    askEmailLabel: 'Your email address, for the reply (optional)',
    askSubmit: 'Send question',
    askCancel: 'Cancel',
    askSent: 'Your question has been sent to your inspection company.',
    askTooShort: 'Please describe your question in a few words.',
    askEmailInvalid: 'Enter a valid email address, or leave the field empty.',
    askTooMany: 'You have just sent several questions. Please try again in an hour.',
    callLabel: 'Call',
    shareTitle: 'Share',
    shareSubtitle:
        'Send the report to your mortgage adviser or estate agent. They do not need an account.',
    shareEmailLabel: 'Email address',
    sharePlaceholder: 'name@example.com',
    shareSubmit: 'Send',
    shareSent: (email) => `The report has been sent to ${email}.`,
    shareInvalid: 'Enter a valid email address.',
    shareDisabled: 'Sharing is switched off for this report. Your inspection company can help.',
    shareTooMany: 'You have just shared the report several times. Please try again in an hour.',
    sharedWith: (name) => `Shared with ${name}`,
    invoiceLabel: 'Invoice',
    invoiceDownload: 'Invoice (PDF)',
    accessCodeTitle: 'Enter your access code',
    accessCodeBody:
        'This report is protected by an access code. You receive the code by text message, separately from the link. No text message? Your inspection company can help.',
    accessCodeLabel: 'Access code',
    accessCodeSubmit: 'Open report',
    accessCodeInvalid:
        'That code is not correct. Check the code in the text message from your inspection company.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1 ? 'You can try once more.' : `You can try ${attempts} more times.`,
    accessCodeEmpty: 'Enter your access code.',
    problemTitles: {
        notFound: 'This link does not work',
        expired: 'This link has expired',
        revoked: 'This link has been withdrawn',
        tooManyAttempts: 'Too many incorrect codes',
        offline: 'No connection',
        unknown: 'The report cannot be shown right now',
    },
    problemBodies: {
        notFound: 'Check that you used the full link from the email or text message.',
        expired: 'Please ask your inspection company for a new link.',
        revoked: 'The inspection company has closed this link. Feel free to ask for a new one.',
        tooManyAttempts:
            'To protect your report you cannot try again for a while. Please try again in fifteen minutes.',
        offline: 'Your device has no internet connection. Please try again shortly.',
        unknown: 'Please try again in a few minutes.',
    },
    expiredOn: (date) =>
        `This link was valid until ${date}. Please ask your inspection company for a new link.`,
    tooManyAttemptsWait: (minutes) =>
        `To protect your report you cannot try again for a while. Please try again in ${minutes === 1 ? 'a minute' : `${minutes} minutes`}.`,
    problemHelp: (phone) => `Get in touch on ${phone}.`,
    problemHelpNoPhone: 'Get in touch with your inspection company.',
    actionFailed: 'That did not work right now. Please try again in a few minutes.',
    retry: 'Try again',
    tabsLabel: 'Report view',
    tabOverview: 'Overview',
    tabCosts: 'Costs',
    tabReport: 'Report',
    tabNhg: 'NHG',
    backToOverview: 'Back to overview',
    backToTop: 'Back to top',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : defects === 1 ? '1 defect' : `${defects} defects`,
            attention === 0
                ? null
                : attention === 1
                  ? '1 attention point'
                  : `${attention} attention points`,
        ].filter((part) => part !== null);

        if (found.length === 0) {
            return 'No defects or attention points were found.';
        }

        return `${found.join(' and ')} ${defects + attention === 1 ? 'was' : 'were'} found.`;
    },
    readSummary: 'Read the full summary',
    outlineIntro: 'Choose an item to read it in the report.',
    judgementLegend: 'The ratings and their colours',
    judgementLegendLink: 'How to read this report',
    costTableTitle: 'Costs per item',
    costTableIntro: 'Choose an item to read the findings behind it in the report.',
    coverInspectedAt: 'Inspection date',
    coverInspector: 'Inspector',
    coverOrderNumber: 'File number',
    coverReport: 'Report',
    coverVersion: (version, date) => (date ? `version ${version}, ${date}` : `version ${version}`),
    partQuiet: 'Nothing to report.',
    costsColumnPart: 'Item',
    costsColumnGroup: 'Cost group',
    costsTotalRow: 'Total',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration:
        'The administrative details and the inspector’s signature are in the PDF report.',
    photoOpen: (caption, position, count) =>
        `View photo ${position} of ${count} large${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Photo',
    photoClose: 'Close photo',
    photoPrevious: 'Previous photo',
    photoNext: 'Next photo',
    photoPosition: (position, count) => `${position} of ${count}`,
    pdfBusy: 'Fetching the PDF…',
    pdfUnavailable: 'The PDF is not available yet; please try again later.',
    filterLabel: 'Filter findings',
    filters: {
        alles: 'All',
        gebreken: 'Defects',
        aandacht: 'Attention points',
        kosten: 'With costs',
    },
    filterStatus: (shown, total, filtered) => {
        const noun = total === 1 ? 'finding' : 'findings';

        return filtered ? `${shown} of ${total} ${noun}` : `${total} ${noun}`;
    },
    filterEmpty: 'No findings in this selection.',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    chapterFindings: (count) => (count === 1 ? '1 finding' : `${count} findings`),
    chapterQuiet: 'Nothing to report',
    appendixTitle: 'Notes and conditions',
    appendixNote:
        'The general notes on this report and the conditions under which this inspection was carried out.',
    costFindings: (count) => (count === 1 ? 'View the finding' : `View the ${count} findings`),
    costBreakdownEyebrow: 'Costs',
    costBreakdownIntro:
        'These findings add up to this amount. Choose one to read it in the report.',
    costBreakdownRest: 'Other items',
    costBreakdownTotal: 'Total',
    costBreakdownClose: 'Close',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: view the findings behind this amount`,
    nhgJump: (code) => `NHG ${code}: go to this row in the NHG annex`,
    translationNotice: 'Automatically translated from Dutch. The Dutch version prevails.',
    translating: 'Translating the report…',
    translationFailed: 'The translation did not work right now. You are reading the Dutch version.',
};

const de: PublicCopy = {
    documentTitle: 'Ihr Prüfbericht',
    languageLabel: 'Sprache',
    secureLabel: 'gesichert',
    loading: 'Ihr Bericht wird geladen',
    reportReady: 'Bericht fertig',
    validUntil: (date) => `Dieser Link funktioniert bis ${date}.`,
    costsTitle: 'Was kostet die Instandsetzung?',
    costsSubtitle:
        'Unverbindliche Schätzungen inklusive MwSt., für Arbeiten durch einen Fachbetrieb.',
    costsTotal: 'Geschätzte Summe',
    costLabels: { direct: 'Sofort', termijn: 'Später', onderhoud: 'Verbesserung' },
    costParts: (count) => (count === 1 ? '1 Position' : `${count} Positionen`),
    nhgTitle: 'NHG-Anhang',
    nhgSubtitle:
        'Für eine Hypothek mit niederländischer Hypothekengarantie (NHG): die notwendigen Kosten je Bauteil, wie im NHG-Musterbericht im PDF.',
    nhgColumns: {
        code: 'Code',
        element: 'Bauteil',
        locations: 'Kapitel',
        direct: 'Sofort notwendig',
        termijn: 'Später notwendig',
    },
    nhgTotal: 'Summe',
    nhgImprovement: 'Verbesserung',
    nhgOutside: (amount) =>
        `${amount} der Kosten im Bericht stehen nicht im Anhang, weil sie zu keinem Bauteil des NHG-Modells gehören oder nicht für NHG aufgenommen wurden. Deshalb ist die Summe hier niedriger als unter „Was kostet die Instandsetzung?“.`,
    nhgEmpty: 'Im NHG-Anhang stehen keine notwendigen Kosten.',
    findingsTitle: 'Wichtigste Feststellungen',
    findingLocation: 'Ort',
    findingsShowAll: (count) => `Alle ${count} Feststellungen anzeigen`,
    findingsShowFewer: 'Weniger Feststellungen anzeigen',
    urgencyLabels: { direct: 'Sofort', termijn: 'Später', onderhoud: 'Verbesserung' },
    partsTitle: 'Alle Bauteile',
    partsNote: 'Im PDF-Bericht ist jedes Bauteil mit Fotos und Erläuterung ausgearbeitet.',
    severityLabels: { goed: 'In Ordnung', aandacht: 'Beachten', gebrek: 'Mangel' },
    energyUsageUnit: 'kWh/m²·Jahr',
    energyLabelPrefix: 'Energieausweis',
    questionsTitle: 'Fragen zum Bericht?',
    inspectorRole: 'Ihr Sachverständiger',
    askOpen: 'Frage stellen',
    askLabel: 'Ihre Frage',
    askPlaceholder: 'Worum geht es bei Ihrer Frage?',
    askEmailLabel: 'Ihre E-Mail-Adresse für die Antwort (freiwillig)',
    askSubmit: 'Frage senden',
    askCancel: 'Abbrechen',
    askSent: 'Ihre Frage wurde an Ihr Prüfunternehmen gesendet.',
    askTooShort: 'Beschreiben Sie Ihre Frage in ein paar Worten.',
    askEmailInvalid: 'Geben Sie eine gültige E-Mail-Adresse ein oder lassen Sie das Feld leer.',
    askTooMany:
        'Sie haben gerade mehrere Fragen gesendet. Versuchen Sie es in einer Stunde erneut.',
    callLabel: 'Anrufen',
    shareTitle: 'Teilen',
    shareSubtitle:
        'Senden Sie den Bericht an Ihren Finanzierungsberater oder Makler. Ein Konto ist nicht nötig.',
    shareEmailLabel: 'E-Mail-Adresse',
    sharePlaceholder: 'name@beispiel.de',
    shareSubmit: 'Senden',
    shareSent: (email) => `Der Bericht wurde an ${email} gesendet.`,
    shareInvalid: 'Geben Sie eine gültige E-Mail-Adresse ein.',
    shareDisabled:
        'Das Weiterleiten ist für diesen Bericht ausgeschaltet. Ihr Prüfunternehmen hilft Ihnen weiter.',
    shareTooMany:
        'Sie haben den Bericht gerade mehrmals weitergeleitet. Versuchen Sie es in einer Stunde erneut.',
    sharedWith: (name) => `Geteilt mit ${name}`,
    invoiceLabel: 'Rechnung',
    invoiceDownload: 'Rechnung (PDF)',
    accessCodeTitle: 'Geben Sie Ihren Zugangscode ein',
    accessCodeBody:
        'Dieser Bericht ist mit einem Zugangscode geschützt. Den Code erhalten Sie per SMS, getrennt vom Link. Keine SMS erhalten? Ihr Prüfunternehmen hilft Ihnen weiter.',
    accessCodeLabel: 'Zugangscode',
    accessCodeSubmit: 'Bericht öffnen',
    accessCodeInvalid:
        'Dieser Code stimmt nicht. Prüfen Sie den Code aus der SMS Ihres Prüfunternehmens.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1 ? 'Sie haben noch einen Versuch.' : `Sie haben noch ${attempts} Versuche.`,
    accessCodeEmpty: 'Geben Sie Ihren Zugangscode ein.',
    problemTitles: {
        notFound: 'Dieser Link funktioniert nicht',
        expired: 'Dieser Link ist abgelaufen',
        revoked: 'Dieser Link wurde zurückgezogen',
        tooManyAttempts: 'Zu oft ein falscher Code',
        offline: 'Keine Verbindung',
        unknown: 'Der Bericht kann derzeit nicht angezeigt werden',
    },
    problemBodies: {
        notFound:
            'Prüfen Sie, ob Sie den vollständigen Link aus der E-Mail oder SMS verwendet haben.',
        expired: 'Bitten Sie Ihr Prüfunternehmen um einen neuen Link.',
        revoked:
            'Das Prüfunternehmen hat diesen Link geschlossen. Fragen Sie gern nach einem neuen Link.',
        tooManyAttempts:
            'Zum Schutz Ihres Berichts ist ein neuer Versuch vorübergehend nicht möglich. Versuchen Sie es in einer Viertelstunde erneut.',
        offline: 'Ihr Gerät hat keine Internetverbindung. Versuchen Sie es gleich erneut.',
        unknown: 'Versuchen Sie es in einigen Minuten erneut.',
    },
    expiredOn: (date) =>
        `Dieser Link war bis ${date} gültig. Bitten Sie Ihr Prüfunternehmen um einen neuen Link.`,
    tooManyAttemptsWait: (minutes) =>
        `Zum Schutz Ihres Berichts ist ein neuer Versuch vorübergehend nicht möglich. Versuchen Sie es in ${minutes === 1 ? 'einer Minute' : `${minutes} Minuten`} erneut.`,
    problemHelp: (phone) => `Melden Sie sich unter ${phone}.`,
    problemHelpNoPhone: 'Melden Sie sich bei Ihrem Prüfunternehmen.',
    actionFailed: 'Das hat gerade nicht geklappt. Versuchen Sie es in einigen Minuten erneut.',
    retry: 'Erneut versuchen',
    tabsLabel: 'Ansicht des Berichts',
    tabOverview: 'Übersicht',
    tabCosts: 'Kosten',
    tabReport: 'Bericht',
    tabNhg: 'NHG',
    backToOverview: 'Zurück zur Übersicht',
    backToTop: 'Nach oben',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : defects === 1 ? '1 Mangel' : `${defects} Mängel`,
            attention === 0 ? null : attention === 1 ? '1 Hinweis' : `${attention} Hinweise`,
        ].filter((part) => part !== null);

        if (found.length === 0) {
            return 'Es wurden keine Mängel oder Hinweise festgestellt.';
        }

        return `Es ${defects + attention === 1 ? 'wurde' : 'wurden'} ${found.join(' und ')} festgestellt.`;
    },
    readSummary: 'Die ganze Zusammenfassung lesen',
    outlineIntro: 'Wählen Sie ein Bauteil, um es im Bericht zu lesen.',
    judgementLegend: 'Die Bewertungen und ihre Farben',
    judgementLegendLink: 'So lesen Sie diesen Bericht',
    costTableTitle: 'Kosten je Bauteil',
    costTableIntro: 'Wählen Sie ein Bauteil, um die Befunde dazu im Bericht zu lesen.',
    coverInspectedAt: 'Datum der Begehung',
    coverInspector: 'Gutachter',
    coverOrderNumber: 'Aktenzeichen',
    coverReport: 'Bericht',
    coverVersion: (version, date) => (date ? `Version ${version}, ${date}` : `Version ${version}`),
    partQuiet: 'Keine Besonderheiten.',
    costsColumnPart: 'Bauteil',
    costsColumnGroup: 'Kostengruppe',
    costsTotalRow: 'Summe',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration:
        'Die Verwaltungsangaben und die Unterschrift des Gutachters stehen im PDF-Bericht.',
    photoOpen: (caption, position, count) =>
        `Foto ${position} von ${count} groß ansehen${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Foto',
    photoClose: 'Foto schließen',
    photoPrevious: 'Vorheriges Foto',
    photoNext: 'Nächstes Foto',
    photoPosition: (position, count) => `${position} von ${count}`,
    pdfBusy: 'Das PDF wird abgerufen…',
    pdfUnavailable: 'Das PDF ist noch nicht verfügbar; bitte versuchen Sie es später erneut.',
    filterLabel: 'Feststellungen filtern',
    filters: {
        alles: 'Alle',
        gebreken: 'Mängel',
        aandacht: 'Hinweise',
        kosten: 'Mit Kosten',
    },
    filterStatus: (shown, total, filtered) => {
        if (filtered) {
            return `${shown} von ${total} ${total === 1 ? 'Feststellung' : 'Feststellungen'}`;
        }

        return `${total} ${total === 1 ? 'Feststellung' : 'Feststellungen'}`;
    },
    filterEmpty: 'Keine Feststellungen in dieser Auswahl.',
    expandAll: 'Alle aufklappen',
    collapseAll: 'Alle zuklappen',
    chapterFindings: (count) => (count === 1 ? '1 Feststellung' : `${count} Feststellungen`),
    chapterQuiet: 'Keine Besonderheiten',
    appendixTitle: 'Erläuterung und Bedingungen',
    appendixNote:
        'Die allgemeine Erläuterung zu diesem Bericht und die Bedingungen, unter denen diese Prüfung durchgeführt wurde.',
    costFindings: (count) =>
        count === 1 ? 'Feststellung ansehen' : `Die ${count} Feststellungen ansehen`,
    costBreakdownEyebrow: 'Kosten',
    costBreakdownIntro:
        'Diese Feststellungen ergeben zusammen diesen Betrag. Wählen Sie eine, um sie im Bericht zu lesen.',
    costBreakdownRest: 'Sonstige Posten',
    costBreakdownTotal: 'Summe',
    costBreakdownClose: 'Schließen',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: die Feststellungen hinter diesem Betrag ansehen`,
    nhgJump: (code) => `NHG ${code}: zu dieser Zeile im NHG-Anhang`,
    translationNotice:
        'Automatisch aus dem Niederländischen übersetzt. Maßgeblich ist die niederländische Fassung.',
    translating: 'Der Bericht wird übersetzt…',
    translationFailed:
        'Die Übersetzung hat gerade nicht geklappt. Sie lesen die niederländische Fassung.',
};

/**
 * Pools kent drie telvormen: 1 element, 2-4 elementy (behalve 12-14), en de rest elementów.
 */
function polishFindings(count: number): string {
    if (count === 1) {
        return '1 ustalenie';
    }

    const few = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);

    return `${count} ${few ? 'ustalenia' : 'ustaleń'}`;
}

/** "1 usterkę", "3 usterki", "12 usterek": de vorm na "Stwierdzono". */
function polishCount(count: number, [one, few, many]: [string, string, string]): string {
    if (count === 1) {
        return `1 ${one}`;
    }

    const isFew = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);

    return `${count} ${isFew ? few : many}`;
}

function polishElements(count: number): string {
    if (count === 1) {
        return '1 element';
    }

    const few = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);

    return `${count} ${few ? 'elementy' : 'elementów'}`;
}

const pl: PublicCopy = {
    documentTitle: 'Pana/Pani raport z przeglądu',
    languageLabel: 'Język',
    secureLabel: 'bezpieczne',
    loading: 'Trwa wczytywanie raportu',
    reportReady: 'Raport gotowy',
    validUntil: (date) => `Ten link działa do ${date}.`,
    costsTitle: 'Ile kosztuje naprawa?',
    costsSubtitle: 'Szacunkowe kwoty z VAT, za naprawę przez wykwalifikowaną firmę.',
    costsTotal: 'Szacowana suma',
    costLabels: { direct: 'Od razu', termijn: 'Później', onderhoud: 'Ulepszenie' },
    costParts: (count) => polishElements(count),
    nhgTitle: 'Załącznik NHG',
    nhgSubtitle:
        'Dla kredytu hipotecznego z gwarancją NHG: niezbędne koszty według elementów, jak we wzorze raportu NHG w pliku PDF.',
    nhgColumns: {
        code: 'Kod',
        element: 'Element',
        locations: 'Rozdział',
        direct: 'Niezbędne od razu',
        termijn: 'Niezbędne później',
    },
    nhgTotal: 'Razem',
    nhgImprovement: 'Ulepszenia',
    nhgOutside: (amount) =>
        `${amount} kosztów z raportu nie ma w załączniku, ponieważ nie należą do żadnego elementu modelu NHG lub nie zostały uwzględnione dla NHG. Dlatego suma jest tu niższa niż w części „Ile kosztuje naprawa?”.`,
    nhgEmpty: 'W załączniku NHG nie ma niezbędnych kosztów.',
    findingsTitle: 'Najważniejsze ustalenia',
    findingLocation: 'Lokalizacja',
    findingsShowAll: (count) => `Pokaż wszystkie ustalenia (${count})`,
    findingsShowFewer: 'Pokaż mniej ustaleń',
    urgencyLabels: { direct: 'Od razu', termijn: 'Później', onderhoud: 'Ulepszenie' },
    partsTitle: 'Wszystkie elementy',
    partsNote: 'W raporcie PDF każdy element jest opisany ze zdjęciami i wyjaśnieniem.',
    severityLabels: { goed: 'Dobry', aandacht: 'Uwaga', gebrek: 'Usterka' },
    energyUsageUnit: 'kWh/m²·rok',
    energyLabelPrefix: 'Klasa energetyczna',
    questionsTitle: 'Pytania o raport?',
    inspectorRole: 'Pana/Pani inspektor',
    askOpen: 'Zadaj pytanie',
    askLabel: 'Pytanie',
    askPlaceholder: 'Czego dotyczy pytanie?',
    askEmailLabel: 'Adres e-mail do odpowiedzi (nieobowiązkowy)',
    askSubmit: 'Wyślij pytanie',
    askCancel: 'Anuluj',
    askSent: 'Pytanie zostało wysłane do firmy przeprowadzającej przegląd.',
    askTooShort: 'Proszę opisać pytanie w kilku słowach.',
    askEmailInvalid: 'Proszę podać poprawny adres e-mail lub zostawić pole puste.',
    askTooMany: 'Wysłano już kilka pytań. Proszę spróbować ponownie za godzinę.',
    callLabel: 'Zadzwoń',
    shareTitle: 'Udostępnianie',
    shareSubtitle:
        'Prześlij raport doradcy kredytowemu lub pośrednikowi. Konto nie jest potrzebne.',
    shareEmailLabel: 'Adres e-mail',
    sharePlaceholder: 'imie@przyklad.pl',
    shareSubmit: 'Wyślij',
    shareSent: (email) => `Raport został wysłany na adres ${email}.`,
    shareInvalid: 'Proszę podać poprawny adres e-mail.',
    shareDisabled:
        'Przesyłanie dalej jest wyłączone dla tego raportu. Firma przeprowadzająca przegląd pomoże.',
    shareTooMany:
        'Raport został właśnie kilka razy przesłany. Proszę spróbować ponownie za godzinę.',
    sharedWith: (name) => `Udostępniono: ${name}`,
    invoiceLabel: 'Faktura',
    invoiceDownload: 'Faktura (PDF)',
    accessCodeTitle: 'Proszę podać kod dostępu',
    accessCodeBody:
        'Ten raport jest chroniony kodem dostępu. Kod przychodzi SMS-em, oddzielnie od linku. Brak SMS-a? Firma przeprowadzająca przegląd pomoże.',
    accessCodeLabel: 'Kod dostępu',
    accessCodeSubmit: 'Otwórz raport',
    accessCodeInvalid: 'Ten kod jest nieprawidłowy. Proszę sprawdzić kod z SMS-a od firmy.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1 ? 'Pozostała jeszcze jedna próba.' : `Pozostało prób: ${attempts}.`,
    accessCodeEmpty: 'Proszę podać kod dostępu.',
    problemTitles: {
        notFound: 'Ten link nie działa',
        expired: 'Ten link wygasł',
        revoked: 'Ten link został wycofany',
        tooManyAttempts: 'Zbyt wiele błędnych kodów',
        offline: 'Brak połączenia',
        unknown: 'Raportu nie można teraz wyświetlić',
    },
    problemBodies: {
        notFound: 'Proszę sprawdzić, czy użyto pełnego linku z e-maila lub SMS-a.',
        expired: 'Proszę poprosić firmę przeprowadzającą przegląd o nowy link.',
        revoked: 'Firma przeprowadzająca przegląd zamknęła ten link. Można poprosić o nowy.',
        tooManyAttempts:
            'Dla ochrony raportu kolejna próba jest chwilowo niemożliwa. Proszę spróbować za kwadrans.',
        offline: 'Urządzenie nie ma połączenia z internetem. Proszę spróbować za chwilę.',
        unknown: 'Proszę spróbować ponownie za kilka minut.',
    },
    expiredOn: (date) =>
        `Ten link był ważny do ${date}. Proszę poprosić firmę przeprowadzającą przegląd o nowy link.`,
    tooManyAttemptsWait: (minutes) =>
        `Dla ochrony raportu kolejna próba jest chwilowo niemożliwa. Proszę spróbować za ${minutes === 1 ? 'minutę' : `${minutes} min`}.`,
    problemHelp: (phone) => `Kontakt: ${phone}.`,
    problemHelpNoPhone: 'Proszę skontaktować się z firmą przeprowadzającą przegląd.',
    actionFailed: 'Nie udało się. Proszę spróbować ponownie za kilka minut.',
    retry: 'Spróbuj ponownie',
    tabsLabel: 'Widok raportu',
    tabOverview: 'Przegląd',
    tabCosts: 'Koszty',
    tabReport: 'Raport',
    tabNhg: 'NHG',
    backToOverview: 'Powrót do przeglądu',
    backToTop: 'Do góry',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : polishCount(defects, ['usterkę', 'usterki', 'usterek']),
            attention === 0 ? null : polishCount(attention, ['uwagę', 'uwagi', 'uwag']),
        ].filter((part) => part !== null);

        return found.length === 0
            ? 'Nie stwierdzono usterek ani uwag.'
            : `Stwierdzono ${found.join(' i ')}.`;
    },
    readSummary: 'Przeczytaj całe podsumowanie',
    outlineIntro: 'Wybierz element, aby przeczytać go w raporcie.',
    judgementLegend: 'Oceny i ich kolory',
    judgementLegendLink: 'Jak czytać ten raport',
    costTableTitle: 'Koszty według elementów',
    costTableIntro: 'Wybierz element, aby przeczytać ustalenia w raporcie.',
    coverInspectedAt: 'Data oględzin',
    coverInspector: 'Inspektor',
    coverOrderNumber: 'Numer sprawy',
    coverReport: 'Raport',
    coverVersion: (version, date) => (date ? `wersja ${version}, ${date}` : `wersja ${version}`),
    partQuiet: 'Brak uwag.',
    costsColumnPart: 'Element',
    costsColumnGroup: 'Grupa kosztów',
    costsTotalRow: 'Razem',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration: 'Dane administracyjne i podpis inspektora znajdują się w raporcie PDF.',
    photoOpen: (caption, position, count) =>
        `Powiększ zdjęcie ${position} z ${count}${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Zdjęcie',
    photoClose: 'Zamknij zdjęcie',
    photoPrevious: 'Poprzednie zdjęcie',
    photoNext: 'Następne zdjęcie',
    photoPosition: (position, count) => `${position} z ${count}`,
    pdfBusy: 'Pobieranie pliku PDF…',
    pdfUnavailable: 'Plik PDF nie jest jeszcze dostępny; prosimy spróbować ponownie później.',
    filterLabel: 'Filtruj ustalenia',
    filters: {
        alles: 'Wszystko',
        gebreken: 'Usterki',
        aandacht: 'Uwagi',
        kosten: 'Z kosztami',
    },
    filterStatus: (shown, total, filtered) =>
        filtered ? `${shown} z ${total} ustaleń` : polishFindings(total),
    filterEmpty: 'Brak ustaleń w tym wyborze.',
    expandAll: 'Rozwiń wszystko',
    collapseAll: 'Zwiń wszystko',
    chapterFindings: (count) => polishFindings(count),
    chapterQuiet: 'Brak uwag',
    appendixTitle: 'Objaśnienia i warunki',
    appendixNote:
        'Ogólne objaśnienia do tego raportu oraz warunki, na jakich przeprowadzono tę inspekcję.',
    costFindings: (count) => (count === 1 ? 'Zobacz ustalenie' : `Zobacz ${polishFindings(count)}`),
    costBreakdownEyebrow: 'Koszty',
    costBreakdownIntro:
        'Te ustalenia razem dają tę kwotę. Wybierz jedno, aby przeczytać je w raporcie.',
    costBreakdownRest: 'Pozostałe pozycje',
    costBreakdownTotal: 'Razem',
    costBreakdownClose: 'Zamknij',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: zobacz ustalenia składające się na tę kwotę`,
    nhgJump: (code) => `NHG ${code}: przejdź do tego wiersza w załączniku NHG`,
    translationNotice:
        'Przetłumaczono automatycznie z języka niderlandzkiego. Wiążąca jest wersja niderlandzka.',
    translating: 'Trwa tłumaczenie raportu…',
    translationFailed: 'Tłumaczenie się teraz nie udało. Czytają Państwo wersję niderlandzką.',
};

const fr: PublicCopy = {
    documentTitle: 'Votre rapport d’inspection',
    languageLabel: 'Langue',
    secureLabel: 'sécurisé',
    loading: 'Chargement de votre rapport',
    reportReady: 'Rapport prêt',
    validUntil: (date) => `Ce lien fonctionne jusqu’au ${date}.`,
    costsTitle: 'Combien coûtent les réparations ?',
    costsSubtitle:
        'Estimations indicatives TVA comprise, pour une réparation par une entreprise qualifiée.',
    costsTotal: 'Total estimé',
    costLabels: { direct: 'Immédiat', termijn: 'À terme', onderhoud: 'Amélioration' },
    costParts: (count) => (count === 1 ? '1 élément' : `${count} éléments`),
    nhgTitle: 'Annexe NHG',
    nhgSubtitle:
        'Pour un prêt hypothécaire avec la garantie hypothécaire néerlandaise (NHG) : les coûts nécessaires par élément, comme dans le modèle de rapport NHG du PDF.',
    nhgColumns: {
        code: 'Code',
        element: 'Élément',
        locations: 'Chapitre',
        direct: 'Nécessaire immédiatement',
        termijn: 'Nécessaire à terme',
    },
    nhgTotal: 'Total logement',
    nhgImprovement: 'Amélioration',
    nhgOutside: (amount) =>
        `${amount} de coûts du rapport ne figurent pas dans l’annexe, car ils ne relèvent d’aucun élément du modèle NHG ou n’ont pas été retenus pour la NHG. C’est pourquoi le total est ici inférieur à celui de « Combien coûtent les réparations ? ».`,
    nhgEmpty: 'L’annexe NHG ne contient aucun coût nécessaire.',
    findingsTitle: 'Principaux constats',
    findingLocation: 'Emplacement',
    findingsShowAll: (count) => `Afficher les ${count} constats`,
    findingsShowFewer: 'Afficher moins de constats',
    urgencyLabels: { direct: 'Immédiat', termijn: 'À terme', onderhoud: 'Amélioration' },
    partsTitle: 'Tous les éléments',
    partsNote: 'Le rapport PDF détaille chaque élément avec photos et explications.',
    severityLabels: { goed: 'Bon', aandacht: 'Point d’attention', gebrek: 'Défaut' },
    energyUsageUnit: 'kWh/m²·an',
    energyLabelPrefix: 'Étiquette énergétique',
    questionsTitle: 'Des questions sur le rapport ?',
    inspectorRole: 'Votre inspecteur',
    askOpen: 'Poser une question',
    askLabel: 'Votre question',
    askPlaceholder: 'Sur quoi porte votre question ?',
    askEmailLabel: 'Votre adresse e-mail, pour la réponse (facultatif)',
    askSubmit: 'Envoyer la question',
    askCancel: 'Annuler',
    askSent: 'Votre question a été envoyée à votre société d’inspection.',
    askTooShort: 'Décrivez votre question en quelques mots.',
    askEmailInvalid: 'Saisissez une adresse e-mail valide ou laissez le champ vide.',
    askTooMany: 'Vous venez d’envoyer plusieurs questions. Veuillez réessayer dans une heure.',
    callLabel: 'Appeler',
    shareTitle: 'Partager',
    shareSubtitle:
        'Envoyez le rapport à votre conseiller hypothécaire ou à votre agent immobilier. Aucun compte n’est nécessaire.',
    shareEmailLabel: 'Adresse e-mail',
    sharePlaceholder: 'nom@exemple.fr',
    shareSubmit: 'Envoyer',
    shareSent: (email) => `Le rapport a été envoyé à ${email}.`,
    shareInvalid: 'Saisissez une adresse e-mail valide.',
    shareDisabled:
        'Le transfert est désactivé pour ce rapport. Votre société d’inspection peut vous aider.',
    shareTooMany:
        'Vous venez de transférer le rapport plusieurs fois. Veuillez réessayer dans une heure.',
    sharedWith: (name) => `Partagé avec ${name}`,
    invoiceLabel: 'Facture',
    invoiceDownload: 'Facture (PDF)',
    accessCodeTitle: 'Saisissez votre code d’accès',
    accessCodeBody:
        'Ce rapport est protégé par un code d’accès. Vous recevez le code par SMS, séparément du lien. Pas reçu de SMS ? Votre société d’inspection peut vous aider.',
    accessCodeLabel: 'Code d’accès',
    accessCodeSubmit: 'Ouvrir le rapport',
    accessCodeInvalid:
        'Ce code n’est pas correct. Vérifiez le code dans le SMS de votre société d’inspection.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1 ? 'Il vous reste une tentative.' : `Il vous reste ${attempts} tentatives.`,
    accessCodeEmpty: 'Saisissez votre code d’accès.',
    problemTitles: {
        notFound: 'Ce lien ne fonctionne pas',
        expired: 'Ce lien a expiré',
        revoked: 'Ce lien a été retiré',
        tooManyAttempts: 'Trop de codes incorrects',
        offline: 'Pas de connexion',
        unknown: 'Le rapport ne peut pas être affiché pour le moment',
    },
    problemBodies: {
        notFound: 'Vérifiez que vous avez utilisé le lien complet de l’e-mail ou du SMS.',
        expired: 'Demandez un nouveau lien à votre société d’inspection.',
        revoked:
            'La société d’inspection a fermé ce lien. N’hésitez pas à demander un nouveau lien.',
        tooManyAttempts:
            'Pour protéger votre rapport, vous ne pouvez pas réessayer pour le moment. Veuillez réessayer dans un quart d’heure.',
        offline: 'Votre appareil n’a pas de connexion internet. Réessayez dans un instant.',
        unknown: 'Veuillez réessayer dans quelques minutes.',
    },
    expiredOn: (date) =>
        `Ce lien était valable jusqu’au ${date}. Demandez un nouveau lien à votre société d’inspection.`,
    tooManyAttemptsWait: (minutes) =>
        `Pour protéger votre rapport, vous ne pouvez pas réessayer pour le moment. Veuillez réessayer dans ${minutes === 1 ? 'une minute' : `${minutes} minutes`}.`,
    problemHelp: (phone) => `Contactez-nous au ${phone}.`,
    problemHelpNoPhone: 'Contactez votre société d’inspection.',
    actionFailed: 'Cela n’a pas fonctionné. Veuillez réessayer dans quelques minutes.',
    retry: 'Réessayer',
    tabsLabel: 'Affichage du rapport',
    tabOverview: 'Aperçu',
    tabCosts: 'Coûts',
    tabReport: 'Rapport',
    tabNhg: 'NHG',
    backToOverview: 'Retour à l’aperçu',
    backToTop: 'Retour en haut',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : defects === 1 ? '1 défaut' : `${defects} défauts`,
            attention === 0
                ? null
                : attention === 1
                  ? '1 point d’attention'
                  : `${attention} points d’attention`,
        ].filter((part) => part !== null);

        if (found.length === 0) {
            return 'Aucun défaut ni point d’attention n’a été constaté.';
        }

        return `${found.join(' et ')} ${defects + attention === 1 ? 'a été constaté' : 'ont été constatés'}.`;
    },
    readSummary: 'Lire tout le résumé',
    outlineIntro: 'Choisissez un élément pour le lire dans le rapport.',
    judgementLegend: 'Les appréciations et leur couleur',
    judgementLegendLink: 'Comment lire ce rapport',
    costTableTitle: 'Coûts par élément',
    costTableIntro: 'Choisissez un élément pour lire les constats correspondants dans le rapport.',
    coverInspectedAt: 'Date de l’inspection',
    coverInspector: 'Inspecteur',
    coverOrderNumber: 'Numéro de dossier',
    coverReport: 'Rapport',
    coverVersion: (version, date) => (date ? `version ${version}, ${date}` : `version ${version}`),
    partQuiet: 'Rien à signaler.',
    costsColumnPart: 'Élément',
    costsColumnGroup: 'Groupe de coûts',
    costsTotalRow: 'Total',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration:
        'Les données administratives et la signature de l’inspecteur figurent dans le rapport PDF.',
    photoOpen: (caption, position, count) =>
        `Agrandir la photo ${position} sur ${count}${caption ? ` : ${caption}` : ''}`,
    photoTitle: 'Photo',
    photoClose: 'Fermer la photo',
    photoPrevious: 'Photo précédente',
    photoNext: 'Photo suivante',
    photoPosition: (position, count) => `${position} sur ${count}`,
    pdfBusy: 'Récupération du PDF…',
    pdfUnavailable: 'Le PDF n’est pas encore disponible ; veuillez réessayer plus tard.',
    filterLabel: 'Filtrer les constats',
    filters: {
        alles: 'Tout',
        gebreken: 'Défauts',
        aandacht: 'Points d’attention',
        kosten: 'Avec coûts',
    },
    filterStatus: (shown, total, filtered) => {
        const noun = total <= 1 ? 'constat' : 'constats';

        return filtered ? `${shown} sur ${total} ${noun}` : `${total} ${noun}`;
    },
    filterEmpty: 'Aucun constat dans cette sélection.',
    expandAll: 'Tout déplier',
    collapseAll: 'Tout replier',
    chapterFindings: (count) => (count === 1 ? '1 constat' : `${count} constats`),
    chapterQuiet: 'Rien à signaler',
    appendixTitle: 'Explications et conditions',
    appendixNote:
        'Les explications générales de ce rapport et les conditions dans lesquelles cette inspection a été réalisée.',
    costFindings: (count) => (count === 1 ? 'Voir le constat' : `Voir les ${count} constats`),
    costBreakdownEyebrow: 'Coûts',
    costBreakdownIntro:
        'Ces constats donnent ensemble ce montant. Choisissez-en un pour le lire dans le rapport.',
    costBreakdownRest: 'Autres postes',
    costBreakdownTotal: 'Total',
    costBreakdownClose: 'Fermer',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount} : voir les constats derrière ce montant`,
    nhgJump: (code) => `NHG ${code} : aller à cette ligne de l’annexe NHG`,
    translationNotice: 'Traduit automatiquement du néerlandais. La version néerlandaise fait foi.',
    translating: 'Le rapport est en cours de traduction…',
    translationFailed:
        'La traduction n’a pas abouti pour le moment. Vous lisez la version néerlandaise.',
};

const es: PublicCopy = {
    documentTitle: 'Su informe de inspección',
    languageLabel: 'Idioma',
    secureLabel: 'seguro',
    loading: 'Cargando su informe',
    reportReady: 'Informe listo',
    validUntil: (date) => `Este enlace funciona hasta el ${date}.`,
    costsTitle: '¿Cuánto cuesta la reparación?',
    costsSubtitle:
        'Estimaciones orientativas con IVA incluido, para una reparación por una empresa cualificada.',
    costsTotal: 'Total estimado',
    costLabels: { direct: 'Inmediato', termijn: 'A plazo', onderhoud: 'Mejora' },
    costParts: (count) => (count === 1 ? '1 elemento' : `${count} elementos`),
    nhgTitle: 'Anexo NHG',
    nhgSubtitle:
        'Para una hipoteca con la Garantía Hipotecaria Nacional neerlandesa (NHG): los costes necesarios por elemento, como en el modelo de informe NHG del PDF.',
    nhgColumns: {
        code: 'Código',
        element: 'Elemento',
        locations: 'Capítulo',
        direct: 'Necesario de inmediato',
        termijn: 'Necesario a plazo',
    },
    nhgTotal: 'Total vivienda',
    nhgImprovement: 'Mejora',
    nhgOutside: (amount) =>
        `${amount} de costes del informe no figuran en el anexo, porque no corresponden a ningún elemento del modelo NHG o no se incluyeron para la NHG. Por eso el total aquí es menor que en «¿Cuánto cuesta la reparación?».`,
    nhgEmpty: 'El anexo NHG no contiene costes necesarios.',
    findingsTitle: 'Hallazgos principales',
    findingLocation: 'Ubicación',
    findingsShowAll: (count) => `Mostrar los ${count} hallazgos`,
    findingsShowFewer: 'Mostrar menos hallazgos',
    urgencyLabels: { direct: 'Inmediato', termijn: 'A plazo', onderhoud: 'Mejora' },
    partsTitle: 'Todos los elementos',
    partsNote: 'El informe PDF detalla cada elemento con fotos y explicaciones.',
    severityLabels: { goed: 'Bien', aandacht: 'Punto de atención', gebrek: 'Defecto' },
    energyUsageUnit: 'kWh/m²·año',
    energyLabelPrefix: 'Etiqueta energética',
    questionsTitle: '¿Preguntas sobre el informe?',
    inspectorRole: 'Su inspector',
    askOpen: 'Hacer una pregunta',
    askLabel: 'Su pregunta',
    askPlaceholder: '¿Sobre qué trata su pregunta?',
    askEmailLabel: 'Su correo electrónico, para la respuesta (opcional)',
    askSubmit: 'Enviar pregunta',
    askCancel: 'Cancelar',
    askSent: 'Su pregunta se ha enviado a su empresa de inspección.',
    askTooShort: 'Describa su pregunta en unas pocas palabras.',
    askEmailInvalid: 'Introduzca un correo electrónico válido o deje el campo vacío.',
    askTooMany: 'Acaba de enviar varias preguntas. Vuelva a intentarlo dentro de una hora.',
    callLabel: 'Llamar',
    shareTitle: 'Compartir',
    shareSubtitle:
        'Envíe el informe a su asesor hipotecario o a su agente inmobiliario. No necesitan una cuenta.',
    shareEmailLabel: 'Correo electrónico',
    sharePlaceholder: 'nombre@ejemplo.es',
    shareSubmit: 'Enviar',
    shareSent: (email) => `El informe se ha enviado a ${email}.`,
    shareInvalid: 'Introduzca un correo electrónico válido.',
    shareDisabled:
        'El reenvío está desactivado para este informe. Su empresa de inspección puede ayudarle.',
    shareTooMany:
        'Acaba de reenviar el informe varias veces. Vuelva a intentarlo dentro de una hora.',
    sharedWith: (name) => `Compartido con ${name}`,
    invoiceLabel: 'Factura',
    invoiceDownload: 'Factura (PDF)',
    accessCodeTitle: 'Introduzca su código de acceso',
    accessCodeBody:
        'Este informe está protegido con un código de acceso. Recibe el código por SMS, aparte del enlace. ¿No ha recibido el SMS? Su empresa de inspección puede ayudarle.',
    accessCodeLabel: 'Código de acceso',
    accessCodeSubmit: 'Abrir el informe',
    accessCodeInvalid:
        'Este código no es correcto. Compruebe el código del SMS de su empresa de inspección.',
    accessCodeAttemptsLeft: (attempts) =>
        attempts === 1 ? 'Le queda un intento.' : `Le quedan ${attempts} intentos.`,
    accessCodeEmpty: 'Introduzca su código de acceso.',
    problemTitles: {
        notFound: 'Este enlace no funciona',
        expired: 'Este enlace ha caducado',
        revoked: 'Este enlace se ha retirado',
        tooManyAttempts: 'Demasiados códigos incorrectos',
        offline: 'Sin conexión',
        unknown: 'El informe no se puede mostrar ahora',
    },
    problemBodies: {
        notFound: 'Compruebe que ha usado el enlace completo del correo o del SMS.',
        expired: 'Pida un enlace nuevo a su empresa de inspección.',
        revoked:
            'La empresa de inspección ha cerrado este enlace. No dude en pedir un enlace nuevo.',
        tooManyAttempts:
            'Para proteger su informe no puede volver a intentarlo por ahora. Vuelva a intentarlo dentro de un cuarto de hora.',
        offline: 'Su dispositivo no tiene conexión a internet. Vuelva a intentarlo en un momento.',
        unknown: 'Vuelva a intentarlo dentro de unos minutos.',
    },
    expiredOn: (date) =>
        `Este enlace era válido hasta el ${date}. Pida un enlace nuevo a su empresa de inspección.`,
    tooManyAttemptsWait: (minutes) =>
        `Para proteger su informe no puede volver a intentarlo por ahora. Vuelva a intentarlo dentro de ${minutes === 1 ? 'un minuto' : `${minutes} minutos`}.`,
    problemHelp: (phone) => `Póngase en contacto en el ${phone}.`,
    problemHelpNoPhone: 'Póngase en contacto con su empresa de inspección.',
    actionFailed: 'No ha funcionado. Vuelva a intentarlo dentro de unos minutos.',
    retry: 'Reintentar',
    tabsLabel: 'Vista del informe',
    tabOverview: 'Resumen',
    tabCosts: 'Costes',
    tabReport: 'Informe',
    tabNhg: 'NHG',
    backToOverview: 'Volver al resumen',
    backToTop: 'Volver arriba',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : defects === 1 ? '1 defecto' : `${defects} defectos`,
            attention === 0
                ? null
                : attention === 1
                  ? '1 punto de atención'
                  : `${attention} puntos de atención`,
        ].filter((part) => part !== null);

        if (found.length === 0) {
            return 'No se han detectado defectos ni puntos de atención.';
        }

        return `Se ${defects + attention === 1 ? 'ha' : 'han'} detectado ${found.join(' y ')}.`;
    },
    readSummary: 'Leer el resumen completo',
    outlineIntro: 'Elija un elemento para leerlo en el informe.',
    judgementLegend: 'Las valoraciones y sus colores',
    judgementLegendLink: 'Cómo leer este informe',
    costTableTitle: 'Costes por elemento',
    costTableIntro: 'Elija un elemento para leer los hallazgos correspondientes en el informe.',
    coverInspectedAt: 'Fecha de la inspección',
    coverInspector: 'Inspector',
    coverOrderNumber: 'Número de expediente',
    coverReport: 'Informe',
    coverVersion: (version, date) => (date ? `versión ${version}, ${date}` : `versión ${version}`),
    partQuiet: 'Sin observaciones.',
    costsColumnPart: 'Elemento',
    costsColumnGroup: 'Grupo de costes',
    costsTotalRow: 'Total',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration:
        'Los datos administrativos y la firma del inspector figuran en el informe PDF.',
    photoOpen: (caption, position, count) =>
        `Ampliar la foto ${position} de ${count}${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Foto',
    photoClose: 'Cerrar la foto',
    photoPrevious: 'Foto anterior',
    photoNext: 'Foto siguiente',
    photoPosition: (position, count) => `${position} de ${count}`,
    pdfBusy: 'Obteniendo el PDF…',
    pdfUnavailable: 'El PDF aún no está disponible; vuelva a intentarlo más tarde.',
    filterLabel: 'Filtrar hallazgos',
    filters: {
        alles: 'Todo',
        gebreken: 'Defectos',
        aandacht: 'Puntos de atención',
        kosten: 'Con costes',
    },
    filterStatus: (shown, total, filtered) => {
        const noun = total === 1 ? 'hallazgo' : 'hallazgos';

        return filtered ? `${shown} de ${total} ${noun}` : `${total} ${noun}`;
    },
    filterEmpty: 'No hay hallazgos en esta selección.',
    expandAll: 'Desplegar todo',
    collapseAll: 'Plegar todo',
    chapterFindings: (count) => (count === 1 ? '1 hallazgo' : `${count} hallazgos`),
    chapterQuiet: 'Sin observaciones',
    appendixTitle: 'Explicación y condiciones',
    appendixNote:
        'La explicación general de este informe y las condiciones en las que se realizó esta inspección.',
    costFindings: (count) => (count === 1 ? 'Ver el hallazgo' : `Ver los ${count} hallazgos`),
    costBreakdownEyebrow: 'Costes',
    costBreakdownIntro: 'Estos hallazgos suman este importe. Elija uno para leerlo en el informe.',
    costBreakdownRest: 'Otras partidas',
    costBreakdownTotal: 'Total',
    costBreakdownClose: 'Cerrar',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: ver los hallazgos detrás de este importe`,
    nhgJump: (code) => `NHG ${code}: ir a esta fila del anexo NHG`,
    translationNotice:
        'Traducido automáticamente del neerlandés. Prevalece la versión en neerlandés.',
    translating: 'El informe se está traduciendo…',
    translationFailed:
        'La traducción no ha funcionado ahora. Está leyendo la versión en neerlandés.',
};

const tr: PublicCopy = {
    documentTitle: 'Ekspertiz raporunuz',
    languageLabel: 'Dil',
    secureLabel: 'güvenli',
    loading: 'Raporunuz yükleniyor',
    reportReady: 'Rapor hazır',
    validUntil: (date) => `Bu bağlantı ${date} tarihine kadar geçerlidir.`,
    costsTitle: 'Onarım ne kadar tutar?',
    costsSubtitle: 'Yetkin bir firma tarafından yapılacak onarım için KDV dahil tahmini tutarlar.',
    costsTotal: 'Tahmini toplam',
    costLabels: { direct: 'Hemen', termijn: 'İleride', onderhoud: 'İyileştirme' },
    costParts: (count) => `${count} bölüm`,
    nhgTitle: 'NHG eki',
    nhgSubtitle:
        'Hollanda Ulusal İpotek Garantisi (NHG) ile alınan bir konut kredisi için: PDF’teki NHG model raporunda olduğu gibi bölüm başına gerekli maliyetler.',
    nhgColumns: {
        code: 'Kod',
        element: 'Bölüm',
        locations: 'Fasıl',
        direct: 'Hemen gerekli',
        termijn: 'İleride gerekli',
    },
    nhgTotal: 'Konut toplamı',
    nhgImprovement: 'İyileştirme',
    nhgOutside: (amount) =>
        `Rapordaki maliyetlerin ${amount} tutarındaki kısmı ekte yer almıyor; bu maliyetler NHG modelinin hiçbir bölümüne ait değil veya NHG için dahil edilmedi. Bu nedenle buradaki toplam, “Onarım ne kadar tutar?” bölümündekinden düşüktür.`,
    nhgEmpty: 'NHG ekinde gerekli bir maliyet yok.',
    findingsTitle: 'Önemli tespitler',
    findingLocation: 'Konum',
    findingsShowAll: (count) => `${count} tespitin tümünü göster`,
    findingsShowFewer: 'Daha az tespit göster',
    urgencyLabels: { direct: 'Hemen', termijn: 'İleride', onderhoud: 'İyileştirme' },
    partsTitle: 'Tüm bölümler',
    partsNote: 'PDF raporunda her bölüm fotoğraflar ve açıklamalarla ayrıntılı olarak yer alır.',
    severityLabels: { goed: 'İyi', aandacht: 'Dikkat noktası', gebrek: 'Kusur' },
    energyUsageUnit: 'kWh/m²·yıl',
    energyLabelPrefix: 'Enerji etiketi',
    questionsTitle: 'Rapor hakkında sorunuz mu var?',
    inspectorRole: 'Eksperiniz',
    askOpen: 'Soru sor',
    askLabel: 'Sorunuz',
    askPlaceholder: 'Sorunuz ne hakkında?',
    askEmailLabel: 'Yanıt için e-posta adresiniz (isteğe bağlı)',
    askSubmit: 'Soruyu gönder',
    askCancel: 'İptal',
    askSent: 'Sorunuz ekspertiz firmanıza gönderildi.',
    askTooShort: 'Lütfen sorunuzu birkaç kelimeyle yazın.',
    askEmailInvalid: 'Geçerli bir e-posta adresi girin veya alanı boş bırakın.',
    askTooMany: 'Az önce birkaç soru gönderdiniz. Lütfen bir saat sonra tekrar deneyin.',
    callLabel: 'Ara',
    shareTitle: 'Paylaş',
    shareSubtitle:
        'Raporu kredi danışmanınıza veya emlakçınıza gönderin. Bunun için bir hesap gerekmez.',
    shareEmailLabel: 'E-posta adresi',
    sharePlaceholder: 'ad@ornek.com',
    shareSubmit: 'Gönder',
    shareSent: (email) => `Rapor ${email} adresine gönderildi.`,
    shareInvalid: 'Geçerli bir e-posta adresi girin.',
    shareDisabled: 'Bu rapor için iletme kapalı. Ekspertiz firmanız size yardımcı olabilir.',
    shareTooMany: 'Raporu az önce birkaç kez ilettiniz. Lütfen bir saat sonra tekrar deneyin.',
    sharedWith: (name) => `Paylaşıldı: ${name}`,
    invoiceLabel: 'Fatura',
    invoiceDownload: 'Fatura (PDF)',
    accessCodeTitle: 'Erişim kodunuzu girin',
    accessCodeBody:
        'Bu rapor bir erişim koduyla korunuyor. Kodu bağlantıdan ayrı olarak SMS ile alırsınız. SMS gelmedi mi? Ekspertiz firmanız size yardımcı olur.',
    accessCodeLabel: 'Erişim kodu',
    accessCodeSubmit: 'Raporu aç',
    accessCodeInvalid:
        'Bu kod doğru değil. Ekspertiz firmanızdan gelen SMS’teki kodu kontrol edin.',
    accessCodeAttemptsLeft: (attempts) => `Kalan deneme hakkı: ${attempts}.`,
    accessCodeEmpty: 'Erişim kodunuzu girin.',
    problemTitles: {
        notFound: 'Bu bağlantı çalışmıyor',
        expired: 'Bu bağlantının süresi doldu',
        revoked: 'Bu bağlantı geri çekildi',
        tooManyAttempts: 'Çok fazla hatalı kod',
        offline: 'Bağlantı yok',
        unknown: 'Rapor şu anda gösterilemiyor',
    },
    problemBodies: {
        notFound: 'E-postadaki veya SMS’teki bağlantının tamamını kullandığınızdan emin olun.',
        expired: 'Ekspertiz firmanızdan yeni bir bağlantı isteyin.',
        revoked: 'Ekspertiz firması bu bağlantıyı kapattı. Yeni bir bağlantı isteyebilirsiniz.',
        tooManyAttempts:
            'Raporunuzu korumak için şu anda tekrar deneyemezsiniz. Lütfen on beş dakika sonra tekrar deneyin.',
        offline: 'Cihazınızın internet bağlantısı yok. Lütfen biraz sonra tekrar deneyin.',
        unknown: 'Lütfen birkaç dakika sonra tekrar deneyin.',
    },
    expiredOn: (date) =>
        `Bu bağlantı ${date} tarihine kadar geçerliydi. Ekspertiz firmanızdan yeni bir bağlantı isteyin.`,
    tooManyAttemptsWait: (minutes) =>
        `Raporunuzu korumak için şu anda tekrar deneyemezsiniz. Lütfen ${minutes} dakika sonra tekrar deneyin.`,
    problemHelp: (phone) => `${phone} numarasından bize ulaşın.`,
    problemHelpNoPhone: 'Ekspertiz firmanızla iletişime geçin.',
    actionFailed: 'Bu işlem şu anda olmadı. Lütfen birkaç dakika sonra tekrar deneyin.',
    retry: 'Tekrar dene',
    tabsLabel: 'Rapor görünümü',
    tabOverview: 'Genel bakış',
    tabCosts: 'Maliyetler',
    tabReport: 'Rapor',
    tabNhg: 'NHG',
    backToOverview: 'Genel bakışa dön',
    backToTop: 'Başa dön',
    headline: (defects, attention) => {
        const found = [
            defects === 0 ? null : `${defects} kusur`,
            attention === 0 ? null : `${attention} dikkat noktası`,
        ].filter((part) => part !== null);

        return found.length === 0
            ? 'Kusur veya dikkat noktası tespit edilmedi.'
            : `${found.join(' ve ')} tespit edildi.`;
    },
    readSummary: 'Özetin tamamını oku',
    outlineIntro: 'Raporda okumak için bir bölüm seçin.',
    judgementLegend: 'Değerlendirmeler ve renkleri',
    judgementLegendLink: 'Bu rapor nasıl okunur',
    costTableTitle: 'Bölüm başına maliyetler',
    costTableIntro: 'Arkasındaki tespitleri raporda okumak için bir bölüm seçin.',
    coverInspectedAt: 'İnceleme tarihi',
    coverInspector: 'Eksper',
    coverOrderNumber: 'Dosya numarası',
    coverReport: 'Rapor',
    coverVersion: (version, date) => (date ? `sürüm ${version}, ${date}` : `sürüm ${version}`),
    partQuiet: 'Kayda değer bir durum yok.',
    costsColumnPart: 'Bölüm',
    costsColumnGroup: 'Maliyet grubu',
    costsTotalRow: 'Toplam',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration: 'İdari bilgiler ve eksperin imzası PDF raporunda yer alır.',
    photoOpen: (caption, position, count) =>
        `Fotoğraf ${position}/${count} büyük göster${caption ? `: ${caption}` : ''}`,
    photoTitle: 'Fotoğraf',
    photoClose: 'Fotoğrafı kapat',
    photoPrevious: 'Önceki fotoğraf',
    photoNext: 'Sonraki fotoğraf',
    photoPosition: (position, count) => `${position}/${count}`,
    pdfBusy: 'PDF getiriliyor…',
    pdfUnavailable: 'PDF henüz hazır değil; lütfen daha sonra tekrar deneyin.',
    filterLabel: 'Tespitleri filtrele',
    filters: {
        alles: 'Tümü',
        gebreken: 'Kusurlar',
        aandacht: 'Dikkat noktaları',
        kosten: 'Maliyetli',
    },
    filterStatus: (shown, total, filtered) =>
        filtered ? `${shown}/${total} tespit` : `${total} tespit`,
    filterEmpty: 'Bu seçimde tespit yok.',
    expandAll: 'Tümünü aç',
    collapseAll: 'Tümünü kapat',
    chapterFindings: (count) => `${count} tespit`,
    chapterQuiet: 'Kayda değer bir durum yok',
    appendixTitle: 'Açıklamalar ve koşullar',
    appendixNote: 'Bu rapora ilişkin genel açıklamalar ve bu incelemenin yapıldığı koşullar.',
    costFindings: (count) => (count === 1 ? 'Tespiti göster' : `${count} tespiti göster`),
    costBreakdownEyebrow: 'Maliyetler',
    costBreakdownIntro:
        'Bu tespitler birlikte bu tutarı oluşturur. Raporda okumak için birini seçin.',
    costBreakdownRest: 'Diğer kalemler',
    costBreakdownTotal: 'Toplam',
    costBreakdownClose: 'Kapat',
    costBreakdownOpen: (label, amount) =>
        `${label} ${amount}: bu tutarın arkasındaki tespitleri göster`,
    nhgJump: (code) => `NHG ${code}: NHG ekindeki bu satıra git`,
    translationNotice: 'Felemenkçeden otomatik olarak çevrilmiştir. Felemenkçe sürüm esastır.',
    translating: 'Rapor çevriliyor…',
    translationFailed: 'Çeviri şu anda yapılamadı. Felemenkçe sürümü okuyorsunuz.',
};

const ar: PublicCopy = {
    documentTitle: 'تقرير المعاينة الخاص بك',
    languageLabel: 'اللغة',
    secureLabel: 'آمن',
    loading: 'جارٍ تحميل تقريرك',
    reportReady: 'التقرير جاهز',
    validUntil: (date) => `يعمل هذا الرابط حتى ${date}.`,
    costsTitle: 'كم تكلّف الإصلاحات؟',
    costsSubtitle: 'تقديرات استرشادية شاملة ضريبة القيمة المضافة لإصلاح تقوم به شركة مختصة.',
    costsTotal: 'المجموع التقديري',
    costLabels: { direct: 'فوري', termijn: 'لاحقًا', onderhoud: 'تحسين' },
    costParts: (count) => `عدد الأجزاء: ${count}`,
    nhgTitle: 'ملحق NHG',
    nhgSubtitle:
        'لقرض عقاري بضمان الرهن العقاري الوطني الهولندي (NHG): التكاليف الضرورية لكل جزء، كما في نموذج تقرير NHG في ملف PDF.',
    nhgColumns: {
        code: 'الرمز',
        element: 'الجزء',
        locations: 'الفصل',
        direct: 'ضروري فورًا',
        termijn: 'ضروري لاحقًا',
    },
    nhgTotal: 'مجموع العقار',
    nhgImprovement: 'تحسين',
    nhgOutside: (amount) =>
        `${amount} من تكاليف التقرير غير مدرجة في الملحق، لأنها لا تتبع أي جزء من نموذج NHG أو لم تُدرج لأغراض NHG. لذلك يكون المجموع هنا أقل منه في «كم تكلّف الإصلاحات؟».`,
    nhgEmpty: 'لا يتضمن ملحق NHG أي تكاليف ضرورية.',
    findingsTitle: 'أهم الملاحظات',
    findingLocation: 'الموقع',
    findingsShowAll: (count) => `عرض جميع الملاحظات (${count})`,
    findingsShowFewer: 'عرض ملاحظات أقل',
    urgencyLabels: { direct: 'فوري', termijn: 'لاحقًا', onderhoud: 'تحسين' },
    partsTitle: 'جميع الأجزاء',
    partsNote: 'يتناول تقرير PDF كل جزء بالتفصيل مع الصور والشرح.',
    severityLabels: { goed: 'جيد', aandacht: 'نقطة انتباه', gebrek: 'عيب' },
    energyUsageUnit: 'كيلوواط ساعة/م²·سنة',
    energyLabelPrefix: 'ملصق الطاقة',
    questionsTitle: 'هل لديك أسئلة عن التقرير؟',
    inspectorRole: 'المفتش الخاص بك',
    askOpen: 'اطرح سؤالًا',
    askLabel: 'سؤالك',
    askPlaceholder: 'عمّ يدور سؤالك؟',
    askEmailLabel: 'بريدك الإلكتروني للرد (اختياري)',
    askSubmit: 'إرسال السؤال',
    askCancel: 'إلغاء',
    askSent: 'أُرسل سؤالك إلى شركة المعاينة.',
    askTooShort: 'يُرجى كتابة سؤالك في بضع كلمات.',
    askEmailInvalid: 'أدخل بريدًا إلكترونيًا صالحًا أو اترك الحقل فارغًا.',
    askTooMany: 'لقد أرسلت للتو عدة أسئلة. يُرجى المحاولة مرة أخرى بعد ساعة.',
    callLabel: 'اتصال',
    shareTitle: 'مشاركة',
    shareSubtitle: 'أرسل التقرير إلى مستشار الرهن العقاري أو الوسيط العقاري. لا يحتاجون إلى حساب.',
    shareEmailLabel: 'البريد الإلكتروني',
    sharePlaceholder: 'name@example.com',
    shareSubmit: 'إرسال',
    shareSent: (email) => `أُرسل التقرير إلى ${email}.`,
    shareInvalid: 'أدخل بريدًا إلكترونيًا صالحًا.',
    shareDisabled: 'إعادة الإرسال متوقفة لهذا التقرير. يمكن لشركة المعاينة مساعدتك.',
    shareTooMany: 'لقد أعدت إرسال التقرير عدة مرات للتو. يُرجى المحاولة مرة أخرى بعد ساعة.',
    sharedWith: (name) => `تمت المشاركة مع ${name}`,
    invoiceLabel: 'الفاتورة',
    invoiceDownload: 'الفاتورة (PDF)',
    accessCodeTitle: 'أدخل رمز الدخول',
    accessCodeBody:
        'هذا التقرير محمي برمز دخول. تتلقى الرمز برسالة نصية منفصلة عن الرابط. لم تصلك الرسالة؟ يمكن لشركة المعاينة مساعدتك.',
    accessCodeLabel: 'رمز الدخول',
    accessCodeSubmit: 'فتح التقرير',
    accessCodeInvalid: 'هذا الرمز غير صحيح. تحقق من الرمز في الرسالة النصية من شركة المعاينة.',
    accessCodeAttemptsLeft: (attempts) => `عدد المحاولات المتبقية: ${attempts}.`,
    accessCodeEmpty: 'أدخل رمز الدخول.',
    problemTitles: {
        notFound: 'هذا الرابط لا يعمل',
        expired: 'انتهت صلاحية هذا الرابط',
        revoked: 'تم سحب هذا الرابط',
        tooManyAttempts: 'رموز غير صحيحة كثيرة',
        offline: 'لا يوجد اتصال',
        unknown: 'لا يمكن عرض التقرير الآن',
    },
    problemBodies: {
        notFound: 'تأكد من أنك استخدمت الرابط الكامل من البريد الإلكتروني أو الرسالة النصية.',
        expired: 'اطلب رابطًا جديدًا من شركة المعاينة.',
        revoked: 'أغلقت شركة المعاينة هذا الرابط. يمكنك طلب رابط جديد.',
        tooManyAttempts:
            'لحماية تقريرك لا يمكنك المحاولة مرة أخرى حاليًا. يُرجى المحاولة بعد ربع ساعة.',
        offline: 'لا يتوفر اتصال بالإنترنت على جهازك. يُرجى المحاولة بعد قليل.',
        unknown: 'يُرجى المحاولة مرة أخرى بعد بضع دقائق.',
    },
    expiredOn: (date) => `كان هذا الرابط صالحًا حتى ${date}. اطلب رابطًا جديدًا من شركة المعاينة.`,
    tooManyAttemptsWait: (minutes) =>
        `لحماية تقريرك لا يمكنك المحاولة مرة أخرى حاليًا. يُرجى المحاولة بعد ${minutes} دقيقة.`,
    problemHelp: (phone) => `تواصل معنا على ${phone}.`,
    problemHelpNoPhone: 'تواصل مع شركة المعاينة.',
    actionFailed: 'لم ينجح ذلك الآن. يُرجى المحاولة مرة أخرى بعد بضع دقائق.',
    retry: 'حاول مرة أخرى',
    tabsLabel: 'عرض التقرير',
    tabOverview: 'نظرة عامة',
    tabCosts: 'التكاليف',
    tabReport: 'التقرير',
    tabNhg: 'NHG',
    backToOverview: 'العودة إلى النظرة العامة',
    backToTop: 'العودة إلى الأعلى',
    headline: (defects, attention) => {
        if (defects === 0 && attention === 0) {
            return 'لم تُرصد عيوب أو نقاط انتباه.';
        }

        return [
            defects === 0 ? null : `عدد العيوب: ${defects}`,
            attention === 0 ? null : `عدد نقاط الانتباه: ${attention}`,
        ]
            .filter((part) => part !== null)
            .join('، ');
    },
    readSummary: 'اقرأ الملخص كاملًا',
    outlineIntro: 'اختر جزءًا لقراءته في التقرير.',
    judgementLegend: 'التقييمات وألوانها',
    judgementLegendLink: 'كيف تقرأ هذا التقرير',
    costTableTitle: 'التكاليف لكل جزء',
    costTableIntro: 'اختر جزءًا لقراءة الملاحظات المتعلقة به في التقرير.',
    coverInspectedAt: 'تاريخ المعاينة',
    coverInspector: 'المفتش',
    coverOrderNumber: 'رقم الملف',
    coverReport: 'التقرير',
    coverVersion: (version, date) => (date ? `الإصدار ${version}، ${date}` : `الإصدار ${version}`),
    partQuiet: 'لا توجد ملاحظات.',
    costsColumnPart: 'الجزء',
    costsColumnGroup: 'مجموعة التكاليف',
    costsTotalRow: 'المجموع',
    nhgRow: (code) => `NHG ${code}`,
    nhgAdministration: 'البيانات الإدارية وتوقيع المفتش موجودة في تقرير PDF.',
    photoOpen: (caption, position, count) =>
        `تكبير الصورة ${position} من ${count}${caption ? `: ${caption}` : ''}`,
    photoTitle: 'صورة',
    photoClose: 'إغلاق الصورة',
    photoPrevious: 'الصورة السابقة',
    photoNext: 'الصورة التالية',
    photoPosition: (position, count) => `${position} من ${count}`,
    pdfBusy: 'جارٍ جلب ملف PDF…',
    pdfUnavailable: 'ملف PDF غير متاح بعد؛ يُرجى المحاولة لاحقًا.',
    filterLabel: 'تصفية الملاحظات',
    filters: {
        alles: 'الكل',
        gebreken: 'العيوب',
        aandacht: 'نقاط الانتباه',
        kosten: 'مع تكاليف',
    },
    filterStatus: (shown, total, filtered) =>
        filtered ? `${shown} من ${total} ملاحظة` : `عدد الملاحظات: ${total}`,
    filterEmpty: 'لا توجد ملاحظات في هذا الاختيار.',
    expandAll: 'توسيع الكل',
    collapseAll: 'طي الكل',
    chapterFindings: (count) => `عدد الملاحظات: ${count}`,
    chapterQuiet: 'لا توجد ملاحظات',
    appendixTitle: 'شروحات وشروط',
    appendixNote: 'الشرح العام لهذا التقرير والشروط التي أُجريت بموجبها هذه المعاينة.',
    costFindings: (count) => (count === 1 ? 'عرض الملاحظة' : `عرض الملاحظات (${count})`),
    costBreakdownEyebrow: 'التكاليف',
    costBreakdownIntro: 'مجموع هذه الملاحظات هو هذا المبلغ. اختر إحداها لقراءتها في التقرير.',
    costBreakdownRest: 'بنود أخرى',
    costBreakdownTotal: 'المجموع',
    costBreakdownClose: 'إغلاق',
    costBreakdownOpen: (label, amount) => `${label} ${amount}: عرض الملاحظات وراء هذا المبلغ`,
    nhgJump: (code) => `NHG ${code}: الانتقال إلى هذا السطر في ملحق NHG`,
    translationNotice: 'تُرجم آليًا من الهولندية. النسخة الهولندية هي المعتمدة.',
    translating: 'جارٍ ترجمة التقرير…',
    translationFailed: 'تعذّرت الترجمة الآن. أنت تقرأ النسخة الهولندية.',
};

export const PUBLIC_COPY: Record<PublicLanguage, PublicCopy> = { nl, en, de, fr, es, pl, tr, ar };

export function copyFor(language: PublicLanguage): PublicCopy {
    return PUBLIC_COPY[language];
}

/** Alleen de talen die dit bedrijf aanbiedt, in vaste volgorde. */
export function availableLanguages(offered: readonly PublicLanguage[]): PublicLanguage[] {
    const allowed = offered.length > 0 ? offered : PUBLIC_LANGUAGES;

    return PUBLIC_LANGUAGES.filter((language) => allowed.includes(language));
}

/**
 * De kantoorpreview van de klantpagina (scherm 12, admin-modus). Dit is de
 * app, dus **jij-vorm**. Alleen het voorbeeldbericht aan de klant staat in de
 * u-vorm: zo krijgt de klant het binnen.
 */
export const previewCopy = {
    screenTitle: 'Klantpagina',
    eyebrow: (opened: number) => `Publieke pagina · geen account nodig · ${opened}× bekeken`,

    copyLink: 'Link kopiëren',
    copied: 'Link gekopieerd',
    copyFailed: 'Kopiëren lukte niet; selecteer de link en kopieer hem zelf.',
    resend: 'Opnieuw versturen',
    resendDone: (n: number) =>
        `Opnieuw verstuurd naar ${n} ${n === 1 ? 'ontvanger' : 'ontvangers'}.`,
    resendNewLink:
        'De oude link werkte niet meer; de ontvangers kregen een nieuwe link, en een code per sms als die nodig is.',
    resendCodeNote:
        'De toegangscode gaat niet opnieuw mee: geef hem zelf door, of maak rechts een nieuwe code.',
    resendPartial: (sent: number, failed: number) =>
        `Opnieuw verstuurd naar ${sent}; ${failed} ${failed === 1 ? 'ontvanger kreeg' : 'ontvangers kregen'} hem niet.`,
    /** De pagina over het hele scherm, zonder browserchroom en zonder de kolom ernaast. */
    openFull: 'Volledig scherm',
    editReport: 'Rapport bijwerken',
    newerVersion: (released: number, newest: number) =>
        `De klant ziet versie ${released}, zoals die is verstuurd. Versie ${newest} gaat pas mee als je hem goedkeurt.`,

    loading: 'Klantpagina',
    noReportTitle: 'Er is nog geen klantpagina voor deze keuring',
    noReportBody:
        'De klantpagina ontstaat zodra het rapport is goedgekeurd en verstuurd. Tot die tijd is er geen link.',
    toReport: 'Naar het rapport',
    errorTitle: 'De klantpagina kon niet geladen worden',

    linkTitle: {
        none: 'Er is nog geen link naar de klantpagina',
        expired: 'De link naar de klantpagina is verlopen',
        revoked: 'De link naar de klantpagina is ingetrokken',
    },
    linkBody: {
        none: 'Maak een link; de klant krijgt hem pas als je het rapport opnieuw verstuurt.',
        expired:
            'De klant ziet nu een pagina die zegt dat de link verlopen is. Maak een nieuwe link en verstuur het rapport opnieuw.',
        revoked:
            'De klant kan het rapport niet meer openen. Maak een nieuwe link en verstuur het rapport opnieuw.',
    },
    createLink: 'Nieuwe link maken',

    previewNote:
        'Dit is een voorbeeld: vragen stellen en doorsturen kan alleen de klant, met zijn eigen link.',
    pdfBusy: 'De PDF wordt opgehaald…',
    pdfFailed: 'De PDF kon niet worden opgehaald. Probeer het zo opnieuw.',
    pdfNotConfigured:
        'De PDF kan nu niet gemaakt worden. Smartkeur lost dit op; daarna komt de PDF vanzelf. Je hoeft zelf niets te doen.',

    phoneTitle: 'Zo komt de link binnen',
    phoneSender: (company: string) => `${company} · nu`,
    phoneMessage: (address: string) =>
        `Uw keuringsrapport van ${address} staat klaar. Bekijk en download het hier:`,
    phoneContact: (company: string, phone: string) => `Vragen? Bel ${company} op ${phone}.`,

    accessTitle: 'Link & toegang',
    rowSent: 'Verzonden',
    rowOpened: 'Geopend',
    rowDownloaded: 'PDF gedownload',
    rowForwarded: 'Doorgestuurd',
    rowQuestions: 'Vragen',
    rowValidUntil: 'Geldig tot',
    notSent: 'nog niet verstuurd',
    notOpened: 'nog niet geopend',
    times: (n: number) => `${n}×`,
    openedTimes: (n: number, last: string) => `${n}× · laatst ${last}`,
    linkExpired: (date: string) => `${date} · verlopen`,
    linkRevoked: 'ingetrokken',
    noLink: 'geen link',

    toggleCosts: 'Kosten tonen aan de klant',
    toggleForward: 'Klant mag zelf doorsturen',
    toggleCode: 'Toegang met extra code',
    toggleCodeOn: 'De klant heeft naast de link een code nodig.',
    newCode: 'Nieuwe code maken',
    codeTitle: 'Nieuwe toegangscode',
    codeWarningSent:
        'Deze code zie je nu één keer. Hij ging per sms naar de ontvangers met een telefoonnummer; geef hem zelf door aan wie geen sms kreeg.',
    codeWarning:
        'Deze code zie je nu één keer en gaat niet vanzelf naar de klant: geef hem zelf door. Een oude code werkt niet meer.',
    copyCode: 'Code kopiëren',
    codeCopied: 'Code gekopieerd',
    codeClose: 'Klaar',
    settingFailed: 'De instelling is niet opgeslagen. Probeer het opnieuw.',
    shareRevoked: 'Deze link is ingetrokken en kan niet meer veranderen. Maak een nieuwe link.',

    eventsTitle: 'Laatste gebeurtenissen',
    eventLine: (label: string, recipient: string | null, at: string) =>
        [label, recipient, at].filter(Boolean).join(' · '),

    whyTitle: 'Waarom dit werkt',
    whyBody:
        'Elke gedeelde link is een kanaal terug: de koper stuurt het rapport zelf door naar zijn makelaar en adviseur.',
    benchmarkShares: 'personen per link',
    benchmarkFollowUp: 'vervolgvraag',
} as const;
